import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Search, Calendar, Clock, User } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import { BLOG_POSTS } from "@/lib/blog-posts";
import { useState, useMemo } from "react";

const CATEGORIES = ["All", "GEO", "Local SEO", "CRO", "Branding"];

export default function BlogPage() {
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return BLOG_POSTS.filter((p) => {
      const cat = active === "All" || p.category === active;
      const q =
        !query ||
        p.title.toLowerCase().includes(query.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(query.toLowerCase());
      return cat && q;
    });
  }, [active, query]);

  const featured = filtered.find((p) => p.featured) || filtered[0];
  const rest = filtered.filter((p) => p !== featured);

  return (
    <PageShell testId="blog-page">
      <section className="relative pt-16 pb-10 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[60rem] h-[60rem] radial-glow opacity-40 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 text-xs font-semibold tracking-widest uppercase">
            EchoWings Journal
          </div>
          <h1 className="mt-6 font-display text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Insights on SEO, GEO &amp;{" "}
            <span className="text-gradient-brand">growth.</span>
          </h1>
          <p className="mt-6 text-lg text-white/65 max-w-2xl mx-auto">
            Field-tested playbooks from inside the EchoWings studio — covering
            search, AI visibility, branding and conversion.
          </p>
        </div>
      </section>

      <section className="relative pb-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <div className="flex flex-wrap gap-2" data-testid="blog-categories">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`blog-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setActive(c)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                    active === c
                      ? "bg-gradient-to-r from-blue-600 to-cyan-400 text-black shadow-[0_8px_24px_rgba(0,229,255,0.3)]"
                      : "bg-white/[0.025] border border-white/10 text-white/70 hover:text-white hover:border-white/20"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="blog-search"
                placeholder="Search articles…"
                className="w-full pl-11 pr-4 py-2.5 bg-white/[0.04] border border-white/10 focus:border-cyan-500/40 focus:outline-none rounded-full text-sm text-white placeholder:text-white/30 transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      {featured && (
        <section className="relative pb-10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured"
              className="group block rounded-3xl border border-white/10 bg-white/[0.025] hover:border-cyan-500/30 overflow-hidden transition-all"
            >
              <div className="grid md:grid-cols-2 gap-0">
                <div className="aspect-[16/10] md:aspect-auto overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                    <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                      Featured
                    </span>
                    <span className="text-white/45">{featured.category}</span>
                  </div>
                  <h2 className="mt-5 font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">
                    {featured.title}
                  </h2>
                  <p className="mt-4 text-white/65 text-lg leading-relaxed">
                    {featured.excerpt}
                  </p>
                  <div className="mt-6 flex items-center gap-4 text-xs text-white/45">
                    <span className="inline-flex items-center gap-1.5">
                      <User className="w-3 h-3" /> {featured.author}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="w-3 h-3" /> {featured.date}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="w-3 h-3" /> {featured.readTime}
                    </span>
                  </div>
                  <div className="mt-7 inline-flex items-center gap-2 text-cyan-400 font-semibold text-sm group-hover:gap-3 transition-all">
                    Read Article <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="section pt-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {rest.length === 0 ? (
            <div className="text-center text-white/45 py-12" data-testid="blog-empty">
              No articles match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p, i) => (
                <motion.article
                  key={p.slug}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  data-testid={`blog-card-${i}`}
                >
                  <Link
                    to={`/blog/${p.slug}`}
                    className="group block rounded-3xl border border-white/10 bg-white/[0.025] overflow-hidden hover:border-cyan-500/30 transition-colors h-full flex flex-col"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
                        {p.category}
                      </div>
                      <h3 className="mt-2 font-display text-xl font-semibold leading-tight">
                        {p.title}
                      </h3>
                      <p className="mt-2 text-sm text-white/55 leading-relaxed flex-1">
                        {p.excerpt}
                      </p>
                      <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between text-xs text-white/45">
                        <span>{p.date}</span>
                        <span className="inline-flex items-center gap-1.5">
                          <Clock className="w-3 h-3" /> {p.readTime}
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
